// src/pages/admin/healthcare/appointmentTypeComponents/FollowUpNotificationsTab.tsx

import React, { useState, useEffect } from "react";
import { Button, Space, Typography, Tag, Modal, Select, InputNumber, Switch } from "antd";
import StatusTag from "../../../../../components/shared/StatusTag";
import InlineLoading from "../../../../../components/shared/InlineLoading";
import EmptyState from "../../../../../components/shared/EmptyState";
import { useNotifications } from "../../../../../context/NotificationContext";
import { PlusOutlined, DeleteOutlined, ClockCircleOutlined, MailOutlined, MessageOutlined, InfoCircleOutlined, DragOutlined } from "@ant-design/icons";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

import {
	fetchAppointmentTypeFollowUps,
	createAppointmentTypeFollowUp,
	deleteAppointmentTypeFollowUp,
	reorderAppointmentTypeFollowUps,
	toggleAppointmentTypeFollowUpActive,
	fetchAvailableFollowUpTemplates,
	AppointmentTypeFollowUp,
} from "../../../../../api/healthcare/Appointment/AppointmentFollowUpApi";

const { Title,Text } = Typography;
const { confirm } = Modal;

interface FollowUpNotificationsTabProps {
	appointmentTypeId: number;
}

interface FollowUpWithTemplate extends AppointmentTypeFollowUp {
	template_name?: string;
	template_type?: string;
	template_subject?: string;
}

const SortableFollowUpCard: React.FC<{
	followUp: FollowUpWithTemplate;
	onToggleActive: (id: number) => void;
	onDelete: (id: number) => void;
	disabled: boolean;
}> = ({ followUp, onToggleActive, onDelete, disabled }) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: followUp.id });

	const style = {
		transform: CSS.Transform.toString(transform),
		transition,
		opacity: isDragging ? 0.5 : 1,
	};

	const getDelayDescription = () => {
		const parts = [];
		if (followUp.delay_days) parts.push(`${followUp.delay_days} day${followUp.delay_days > 1 ? "s" : ""}`);
		if (followUp.delay_hours) parts.push(`${followUp.delay_hours} hour${followUp.delay_hours > 1 ? "s" : ""}`);
		return parts.join(" and ") || "Not set";
	};

	return (
		<div ref={setNodeRef} style={style}>
			<div
				style={{
					marginBottom: 12,
					borderRadius: 8,
					padding: "12px 16px",
					background: "var(--mg-bg-elevated)",
					border: followUp.is_active ? "1px solid var(--mg-success)" : "1px solid var(--mg-border)",
				}}
			>
				<div style={{ display: "flex", alignItems: "start", gap: 12 }}>
					<div
						{...attributes}
						{...listeners}
						style={{
							cursor: disabled ? "not-allowed" : "grab",
							padding: "4px 0",
							opacity: disabled ? 0.5 : 1,
						}}
					>
						<DragOutlined style={{ fontSize: 16, color: "var(--mg-text-secondary)" }} />
					</div>

					<div style={{ flex: 1 }}>
						<div style={{ marginBottom: 8 }}>
							<Text strong>{followUp.template_name || `Template #${followUp.notification_template_id}`}</Text>
							<Space style={{ marginLeft: 8 }}>
							<StatusTag status={followUp.is_active ? "active" : "inactive"} />
								{followUp.template_type === "email" && (
									<Tag icon={<MailOutlined />} color="blue">
										Email
									</Tag>
								)}
								{followUp.template_type === "sms" && (
									<Tag icon={<MessageOutlined />} color="green">
										SMS
									</Tag>
								)}
								{followUp.template_type === "both" && (
									<Tag icon={<MailOutlined />} color="purple">
										Both
									</Tag>
								)}
							</Space>
						</div>

						{followUp.template_subject && (
							<Text type="secondary" style={{ fontSize: 12, display: "block", marginBottom: 8 }}>
								{followUp.template_subject}
							</Text>
						)}

						<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
							<div style={{ display: "flex", alignItems: "center", gap: 4 }}>
								<ClockCircleOutlined style={{ fontSize: 12, color: "var(--mg-text-secondary)" }} />
								<Text type="secondary" style={{ fontSize: 11 }}>
									Send {getDelayDescription()} after appointment
								</Text>
							</div>
							<Text type="secondary" style={{ fontSize: 11 }}>
								Order: #{followUp.sort_order + 1}
							</Text>
						</div>
					</div>

					<Space direction="vertical" size="small" style={{ alignItems: "flex-end" }}>
						<Switch size="small" checked={followUp.is_active} onChange={() => onToggleActive(followUp.id)} disabled={disabled} />
						<Button danger size="small" icon={<DeleteOutlined />} onClick={() => onDelete(followUp.id)} disabled={disabled}>
							Remove
						</Button>
					</Space>
				</div>
			</div>
		</div>
	);
};

const FollowUpNotificationsTab: React.FC<FollowUpNotificationsTabProps> = ({ appointmentTypeId }) => {
	const { showSuccess, showError } = useNotifications();
	const [followUps, setFollowUps] = useState<FollowUpWithTemplate[]>([]);
	const [loading, setLoading] = useState<boolean>(false);
	const [modalVisible, setModalVisible] = useState<boolean>(false);
	const [availableTemplates, setAvailableTemplates] = useState<any[]>([]);
	const [selectedTemplateId, setSelectedTemplateId] = useState<number | null>(null);
	const [delayDays, setDelayDays] = useState<number | undefined>(1);
	const [delayHours, setDelayHours] = useState<number | undefined>(0);

	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	useEffect(() => {
		loadFollowUps();
		loadAvailableTemplates();
	}, [appointmentTypeId]);

	const loadFollowUps = async () => {
		setLoading(true);
		try {
			const data = await fetchAppointmentTypeFollowUps(appointmentTypeId);

			// Fetch template details for each follow-up
			const templates = await fetchAvailableFollowUpTemplates();
			const followUpsWithTemplates = data.map((followUp) => {
				const template = templates.find((t: any) => t.id === followUp.notification_template_id);
				return {
					...followUp,
					template_name: template?.name,
					template_type: template?.type,
					template_subject: template?.subject,
				};
			});

			setFollowUps(followUpsWithTemplates.sort((a, b) => a.sort_order - b.sort_order));
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to load follow-up notifications");
		} finally {
			setLoading(false);
		}
	};

	const loadAvailableTemplates = async () => {
		try {
			const templates = await fetchAvailableFollowUpTemplates();
			setAvailableTemplates(Array.isArray(templates) ? templates : []);
		} catch (error: any) {
			showError("Failed to load available templates");
		}
	};

	const handleOpenModal = () => {
		setSelectedTemplateId(null);
		setDelayDays(1);
		setDelayHours(0);
		setModalVisible(true);
	};

	const handleCreateFollowUp = async () => {
		if (!selectedTemplateId) {
			showError("Please select a template");
			return;
		}

		if (!delayDays && !delayHours) {
			showError("Please specify delay days or hours");
			return;
		}

		setLoading(true);
		try {
			await createAppointmentTypeFollowUp(appointmentTypeId, {
				notification_template_id: selectedTemplateId,
				delay_days: delayDays,
				delay_hours: delayHours,
				is_active: true,
				sort_order: followUps.length,
			});

			showSuccess("Follow-up notification added successfully");
			setModalVisible(false);
			await loadFollowUps();
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to create follow-up notification");
		} finally {
			setLoading(false);
		}
	};

	const handleToggleActive = async (id: number) => {
		setLoading(true);
		try {
			await toggleAppointmentTypeFollowUpActive(appointmentTypeId, id);
			showSuccess("Follow-up notification updated");
			await loadFollowUps();
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to update follow-up notification");
		} finally {
			setLoading(false);
		}
	};

	const handleDelete = (id: number) => {
		confirm({
			title: "Remove Follow-up Notification?",
			content: "Are you sure you want to remove this follow-up notification?",
			okText: "Remove",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				setLoading(true);
				try {
					await deleteAppointmentTypeFollowUp(appointmentTypeId, id);
					showSuccess("Follow-up notification removed successfully");
					await loadFollowUps();
				} catch (error: any) {
					showError(error.response?.data?.message || "Failed to remove follow-up notification");
				} finally {
					setLoading(false);
				}
			},
		});
	};

	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;

		if (!over || active.id === over.id) {
			return;
		}

		const oldIndex = followUps.findIndex((item) => item.id === active.id);
		const newIndex = followUps.findIndex((item) => item.id === over.id);

		const newFollowUps = arrayMove(followUps, oldIndex, newIndex).map((item, index) => ({
			...item,
			sort_order: index,
		}));

		setFollowUps(newFollowUps);

		try {
			await reorderAppointmentTypeFollowUps(appointmentTypeId, {
				follow_ups: newFollowUps.map((item) => ({
					id: item.id,
					sort_order: item.sort_order,
				})),
			});
			showSuccess("Follow-up order updated");
		} catch (error: any) {
			showError("Failed to reorder follow-ups");
			await loadFollowUps(); // Reload to revert changes
		}
	};

	const getAvailableTemplatesForSelection = () => {
		const assignedTemplateIds = followUps.map((f) => f.notification_template_id);
		return availableTemplates.filter((t) => !assignedTemplateIds.includes(t.id));
	};

	return (
		<div>
			<div style={{ marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
				<div>
					<Title level={5} style={{ margin: 0, marginBottom: 4 }}>
						Follow-up Notifications
					</Title>
					<Text type="secondary" style={{ fontSize: 12 }}>
						Automatically send notifications to patients after appointments
					</Text>
				</div>
				<Button type="primary" icon={<PlusOutlined />} size="small" onClick={handleOpenModal} disabled={loading}>
					Add Follow-up
				</Button>
			</div>

			{loading ? <InlineLoading minHeight={160} message="Loading follow-up notifications…" /> : <>
				{followUps.length === 0 ? (
					<EmptyState
						title="No follow-up notifications configured"
						description='Click "Add Follow-up" to automatically send notifications after appointments'
					/>
				) : (
					<>
						<div style={{ marginBottom: 12, padding: 8, borderRadius: 6, background: "var(--mg-bg-subtle)", border: "1px solid var(--mg-border)" }}>
							<Space size="small">
								<InfoCircleOutlined style={{ color: "var(--mg-primary)" }} />
								<Text style={{ fontSize: 12 }}>Drag and drop to reorder. Notifications will be sent in the order shown.</Text>
							</Space>
						</div>

						<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
							<SortableContext items={followUps.map((f) => f.id)} strategy={verticalListSortingStrategy}>
								{followUps.map((followUp) => (
									<SortableFollowUpCard key={followUp.id} followUp={followUp} onToggleActive={handleToggleActive} onDelete={handleDelete} disabled={loading} />
								))}
							</SortableContext>
						</DndContext>
					</>
				)}
			</>}

			<Modal
				title="Add Follow-up Notification"
				open={modalVisible}
				onCancel={() => setModalVisible(false)}
				onOk={handleCreateFollowUp}
				okText="Add"
				okButtonProps={{ disabled: !selectedTemplateId || (!delayDays && !delayHours), loading }}
				width={600}
			>
				<Space direction="vertical" style={{ width: "100%" }} size="middle">
					<div>
						<label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 500 }}>Notification Template</label>
						<Select
							placeholder="Select a follow-up template"
							value={selectedTemplateId}
							onChange={setSelectedTemplateId}
							style={{ width: "100%" }}
							size="middle"
							showSearch
							filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())}
							options={getAvailableTemplatesForSelection().map((t) => ({
								label: t.name,
								value: t.id,
							}))}
						/>
						{getAvailableTemplatesForSelection().length === 0 && (
							<Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
								All available follow-up templates have been assigned. Create new templates in Settings.
							</Text>
						)}
					</div>

					<div>
						<label style={{ display: "block", marginBottom: 8, fontSize: 13, fontWeight: 500 }}>Send Notification After</label>
						<div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
							<div>
								<label style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Days</label>
								<InputNumber min={0} max={365} value={delayDays} onChange={(value) => setDelayDays(value ? Number(value) : undefined)} style={{ width: "100%" }} placeholder="e.g., 7" />
							</div>
							<div>
								<label style={{ display: "block", marginBottom: 4, fontSize: 12 }}>Hours</label>
								<InputNumber min={0} max={168} value={delayHours} onChange={(value) => setDelayHours(value ? Number(value) : undefined)} style={{ width: "100%" }} placeholder="e.g., 24" />
							</div>
						</div>
						<Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 4 }}>
							The notification will be sent this long after the appointment ends
						</Text>
					</div>

					{selectedTemplateId && (
						<div style={{ padding: 12, borderRadius: 6, background: "var(--mg-bg-subtle)", border: "1px solid var(--mg-border)" }}>
							<Text strong style={{ fontSize: 12, display: "block", marginBottom: 4 }}>
								Preview:
							</Text>
							<Text style={{ fontSize: 12 }}>
								{(() => {
									const template = availableTemplates.find((t) => t.id === selectedTemplateId);
									return template?.name || "Selected template";
								})()}
							</Text>
							<br />
							<Text type="secondary" style={{ fontSize: 11 }}>
								Will be sent{" "}
								{(() => {
									const parts = [];
									if (delayDays) parts.push(`${delayDays} day${delayDays > 1 ? "s" : ""}`);
									if (delayHours) parts.push(`${delayHours} hour${delayHours > 1 ? "s" : ""}`);
									return parts.join(" and ") || "immediately";
								})()}{" "}
								after appointment end
							</Text>
						</div>
					)}
				</Space>
			</Modal>
		</div>
	);
};

export default FollowUpNotificationsTab;
